import { useEffect, useState } from 'react';
import { Drawer } from '@mui/material';
import { clsx } from 'clsx';
import { Button } from '../../../../button/Button';
import { Autocomplete } from '../../../../input/Autocomplete/Autocomplete';
import { SearchArea } from '../../../../input/SearchArea/SearchArea';
import { PaginationData } from '../../../../pagination-controls/interfaces';
import { PaginationControls } from '../../../../pagination-controls/PaginationControls';
import { EntityUIState, MutationState } from '../../../../request/interfaces/entity-ui-state';
import { getErrorMessageFromAxiosError } from '../../../../request/utils';
import { EmptyWidget } from '../../../../shared/components/EmptyWidget/EmptyWidget';
import { FieldsTable } from './FieldsTable';
import { RetryApiAlert } from '../../../../shared/components/RetryApiAlert/RetryApiAlert';
import { Integration } from '../../../../shared/interfaces/integration';
import { getDataIdGenerator } from '../../../../shared/utils/dataId';
import { TableShimmer } from '../../../../table/TableShimmer';
import { ObjectConfig } from '../../interfaces/object';
import { FieldsConfigSourceDestTableNameView } from './FieldsConfigSourceDestTableNameView';
import { useFieldsConfig } from './useFieldsConfig';
import {
  ObjectConfigOptionTypeOption,
  ObjectFieldsMetaObjectConfig
} from '../../interfaces/object-field-header';
import styles from './FieldsConfig.module.scss';

export interface FieldsConfigProps {
  integration: Integration;
  isViewMode: boolean;
  object: ObjectConfig;
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  onApplyChanges: (object: ObjectConfig) => void;
}

export function FieldsConfig(props: FieldsConfigProps) {
  const { open, ...remaining } = props;

  return (
    <Drawer
      PaperProps={{
        style: {
          minWidth: '1100px',
          maxWidth: '1440px',
          width: '80%'
        },
        sx: {
          boxShadow: 'var(--shadow-default)',
          backgroundColor: 'var(--surface-bg-color)',
          alignItems: 'unset',
          transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
        }
      }}
      open={open}
      onClose={() => remaining.setOpen(false)}
      anchor='right'
    >
      <FieldsConfigInner {...remaining} />
    </Drawer>
  );
}
export function FieldsConfigInner({
  integration,
  isViewMode,
  object,
  setOpen,
  onApplyChanges
}: Omit<FieldsConfigProps, 'open'>) {
  const {
    listConductor,
    destinationTableName,
    fieldsMetadata,
    fieldsObjectConfig,
    filteredFields,
    fieldsUIState,
    fetchFieldsError,
    fetchFieldsList,
    selectedCount,
    filteredListSelectedCount,
    totalCount,
    isListIncluded,
    isListIndeterminate,
    mutationState,
    updateFieldProperty,
    changeCheckedState,
    changeSelectAllCheckedState,
    nextClickHandler,
    previousClickHandler,
    onSearch,
    applyChanges,
    closeFieldsConfig,
    onChangeObjectConfigMetaOption
  } = useFieldsConfig({
    integration,
    object,
    isViewMode,
    onSave: o => {
      setOpen(false);
      onApplyChanges(o);
    },
    onClose: () => setOpen(false)
  });

  useEffect(() => {
    if (object) {
      fetchFieldsList();
    }
  }, [object]);

  const dataIdGenerator = getDataIdGenerator('FieldsConfigContainer');

  const fetchFieldsErrorMessage = fetchFieldsError
    ? getErrorMessageFromAxiosError(fetchFieldsError)
    : 'Retry Fetching the Fields';

  return (
    <div className={styles.objectFieldsModal}>
      <div className='border-bottom'>
        <div className='center-flex-row justify-between p-5'>
          <div className='d-flex flex-col'>
            <span className='text-title-3 mb-1' data-id={dataIdGenerator('title')}>
              {isViewMode ? 'Object Fields' : 'Field Selection'}
            </span>

            <span className='text-secondary' data-id={dataIdGenerator('subtitle')}>
              {isViewMode
                ? 'The fields of the Source object.'
                : 'Modify field selection to update the schema.'}
            </span>
          </div>

          {fieldsMetadata ? (
            <div className='center-flex-row'>
              {fieldsMetadata.objectConfigs.map((item, index) => (
                <ObjectFieldMetaOptions
                  key={item.key}
                  item={item}
                  metaValue={fieldsObjectConfig[item.key] as string}
                  className={clsx(index > 0 && 'ml-5')}
                  onChange={v => onChangeObjectConfigMetaOption(item.key, v)}
                  disabled={fieldsUIState === EntityUIState.LOADING}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className='px-5 py-3 center-flex-row justify-between border-bottom w-100'>
        <div className='w-65 mr-8'>
          {destinationTableName ? (
            <FieldsConfigSourceDestTableNameView
              sourceTableName={object?.k0 as string}
              destinationTableName={destinationTableName}
            />
          ) : null}
        </div>

        <div className='center-flex-row'>
          <SearchArea
            dataId={dataIdGenerator('')}
            placeholder='Search Fields'
            maxWidth='200px'
            collapsible={false}
            autofocus
            defaultSearch=''
            showOperators={false}
            onSearch={onSearch}
            searchWrapperClassName={styles.searchBar}
          />

          {fieldsUIState === EntityUIState.IDLE || fieldsUIState === EntityUIState.REFRESHING ? (
            <>
              <div className={styles.separator} />

              <PaginationControls
                paginationData={listConductor?.getListMeta().pagination as PaginationData}
                listState={fieldsUIState}
                nextPage={nextClickHandler}
                prevPage={previousClickHandler}
                dataId={dataIdGenerator('')}
                trackingProps={{
                  pipelineId: integration.id,
                  widget: 'ObjectConfigFields'
                }}
              />
            </>
          ) : null}
        </div>
      </div>

      <div className='flex-1 overflow-hidden' data-id={dataIdGenerator('')}>
        {fieldsUIState === EntityUIState.LOADING ? (
          <TableShimmer
            rowsCount={5}
            columnsCount={7}
            tableProps={{ withoutShadow: true, compactHeader: true }}
          />
        ) : null}

        {fieldsUIState === EntityUIState.IDLE ||
        (filteredFields?.length && fieldsUIState === EntityUIState.REFRESHING) ? (
          <FieldsTable
            pipeline={integration}
            selectedCount={filteredListSelectedCount}
            totalCount={totalCount}
            isViewMode={isViewMode}
            isListIndeterminate={isListIndeterminate}
            isListIncluded={isListIncluded}
            fieldHeaders={fieldsMetadata}
            filteredFields={filteredFields}
            updateFieldProperty={updateFieldProperty}
            changeSelectAllCheckedState={changeSelectAllCheckedState}
            changeCheckedState={changeCheckedState}
          />
        ) : null}

        {fieldsUIState === EntityUIState.EMPTY || fieldsUIState === EntityUIState.NEW ? (
          <EmptyWidget icon='database' title='No Fields Found' className='h-100 w-100' />
        ) : null}

        {fieldsUIState === EntityUIState.ERRORED ? (
          <RetryApiAlert
            title='Error while fetching Fields'
            trackingProps={{
              widget: 'ObjectFieldsConfig',
              error: fetchFieldsErrorMessage
            }}
            subTitle={fetchFieldsErrorMessage}
            secondaryCTAProps={{
              onClick: fetchFieldsList
            }}
            className='h-100 w-100'
          />
        ) : null}
      </div>

      <div className={styles.formActionContainer}>
        <Button
          variation='outline'
          onClick={closeFieldsConfig}
          disabled={false}
          dataId={dataIdGenerator('cancel')}
        >
          {isViewMode ? 'Close' : 'Cancel'}
        </Button>

        {!isViewMode ? (
          <Button
            disabled={
              selectedCount === 0 ||
              mutationState === MutationState.MUTATING ||
              fieldsUIState === EntityUIState.LOADING
            }
            showProgress={mutationState === MutationState.MUTATING}
            className='ml-3'
            onClick={applyChanges}
            dataId={dataIdGenerator('continue')}
          >
            Apply Changes
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function ObjectFieldMetaOptions({
  item,
  className,
  metaValue,
  onChange,
  disabled
}: {
  item: ObjectFieldsMetaObjectConfig;
  className?: string;
  metaValue: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState<ObjectConfigOptionTypeOption | undefined>();

  useEffect(() => {
    setValue(item.options.find(option => option.value === metaValue));
  }, [metaValue, item]);

  return (
    <Autocomplete
      dataId={item.key}
      textFieldProps={{
        label: item.displayName,
        placeholder: item.displayName
      }}
      options={item.options}
      value={value}
      className={className}
      sx={{ width: 300 }}
      disableClearable
      disabled={disabled || !item.isEditable}
      onChange={(_, v) => {
        setValue(v as ObjectConfigOptionTypeOption);
        onChange((v as ObjectConfigOptionTypeOption).value);
      }}
    />
  );
}
